import type { HierarchyNode} from '@bayer-int/portletce-shared';
import { findMap, head, isNotNullable, zip } from '@bayer-int/portletce-shared';
import type { Observable} from 'rxjs';
import { forkJoin, of, throwError } from 'rxjs';
import { catchError, defaultIfEmpty, map, switchMap } from 'rxjs/operators';
import { groupBy, isString } from 'lodash-es';

import type { HttpError} from 'portlet/common/errors';
import { createInteractionError, InteractionError } from 'portlet/common/errors';
import {
    addAncestorsOfSelectedHierarchyNodes,
    adjustFilterEffectInHierarchy,
} from 'portlet/common/utils/mergeFilterNodes';
import type {
    FilterData,
    Keyword,
    ResolvedListenToDefinition,
    ResolvedListenToDefinitions,
    HierarchyFilterNode,
    ResolvedFilterHint,
    FilterDataArray} from 'portlet/models';
import {
    FilterValue,
    MatchedListenToQueryContext
} from 'portlet/models';
import type { Hierarchy, MasterDataService } from 'portlet/services';

import { compareKeywords } from './keywords';

/**
 * Manages unstructured user interaction payload, and tries to coerce it into
 * specific FilterValue and FilterData based on the FilterHint defined on the ResolvedListenToDefinition.
 */
export class InteractionFilterTypeHintProcessor {
    constructor(private readonly masterdata: MasterDataService) {}

    public processFilterTypeHint(
        matchedFilterItem: ResolvedListenToDefinition,
        allFilterItems: ResolvedListenToDefinitions,
        filterDataArray: FilterDataArray,
        keys: string[],
        eventKeywords: Keyword[],
        settings: {
            applyFilterToUpstreamHierarchyLevels: boolean;
        },
        queryContext: MatchedListenToQueryContext = MatchedListenToQueryContext.default(),
    ): Observable<FilterData[]> {
        const err = () =>
            new InteractionError(
                { matchedFilterItem, keys, eventKeywords },
                `Cannot resolve interaction to filter`,
            );

        if (!matchedFilterItem.filterHint) return throwError(() => err());
        if (matchedFilterItem.filterHint.dimension)
            return this.processDimensionFilterTypeHint(
                matchedFilterItem,
                allFilterItems,
                matchedFilterItem.filterHint.dimension,
                filterDataArray,
                keys,
                eventKeywords,
                queryContext,
                settings,
            );
        if (matchedFilterItem.filterHint.hierarchy)
            return this.processHierarchyFilterTypeHint(
                matchedFilterItem,
                allFilterItems,
                matchedFilterItem.filterHint.hierarchy,
                filterDataArray,
                keys,
                eventKeywords,
                queryContext,
                settings,
            );

        if (matchedFilterItem.filterHint.date)
            return this.processDateFilterTypeHint(
                matchedFilterItem,
                allFilterItems,
                matchedFilterItem.filterHint.date,
                filterDataArray,
                keys,
                eventKeywords,
                queryContext,
                settings,
            );
        if (matchedFilterItem.filterHint.dateRange)
            return this.processDateRangeFilterTypeHint(
                matchedFilterItem,
                allFilterItems,
                matchedFilterItem.filterHint.dateRange,
                filterDataArray,
                keys,
                eventKeywords,
                queryContext,
                settings,
            );
        if (matchedFilterItem.filterHint.scale)
            return this.processScaleFilterTypeHint(
                matchedFilterItem,
                allFilterItems,
                matchedFilterItem.filterHint.scale,
                filterDataArray,
                keys,
                eventKeywords,
                queryContext,
                settings,
            );
        if (matchedFilterItem.filterHint.smartViewTag)
            return this.processSmartViewTagFilterTypeHint(
                matchedFilterItem,
                allFilterItems,
                matchedFilterItem.filterHint.smartViewTag,
                filterDataArray,
                keys,
                eventKeywords,
                queryContext,
                settings,
            );
        if (matchedFilterItem.filterHint.role)
            return this.processRoleFilterTypeHint(
                matchedFilterItem,
                allFilterItems,
                matchedFilterItem.filterHint.role,
                filterDataArray,
                keys,
                eventKeywords,
                queryContext,
                settings,
            );

        return throwError(() => err());
    }

    private processDimensionFilterTypeHint(
        matchedFilterItem: ResolvedListenToDefinition,
        allFilterItems: ResolvedListenToDefinitions,
        hint: Exclude<ResolvedFilterHint['dimension'], undefined>,
        _filterDataArray: FilterDataArray,
        keys: string[],
        _eventKeywords: Keyword[],
        _queryContext: MatchedListenToQueryContext,
        _settings: {
            applyFilterToUpstreamHierarchyLevels: boolean;
        },
    ): Observable<FilterData[]> {
        return this.masterdata.getDimensionNodes(hint.internalKey, keys).pipe(
            map(value => FilterValue.DIMENSION({ exclude: false, value })),
            map(value => [{ item: matchedFilterItem.item, value }]),
        );
    }

    private processHierarchyFilterTypeHint(
        matchedFilterItem: ResolvedListenToDefinition,
        allFilterItems: ResolvedListenToDefinitions,
        hint: Exclude<ResolvedFilterHint['hierarchy'], undefined>,
        filterDataArray: FilterDataArray,
        keys: string[],
        eventKeywords: Keyword[],
        queryContext: MatchedListenToQueryContext,
        settings: {
            applyFilterToUpstreamHierarchyLevels: boolean;
        },
    ): Observable<FilterData[]> {
        return forkJoin({
            mainHierarchyFilter: this.processHierarchyFilterTypeHintForSingleHierarchy(
                matchedFilterItem,
                hint,
                filterDataArray,
                keys,
                eventKeywords,
                queryContext,
                settings,
            ),
            intersectingHierarchies: this.masterdata.getIntersectingHierarchies(hint.id),
        }).pipe(
            switchMap(({ mainHierarchyFilter, intersectingHierarchies }) => {
                if (!FilterValue.is.HIERARCHY(mainHierarchyFilter.value))
                    return of([mainHierarchyFilter]);

                const mainHierarchyNodes = mainHierarchyFilter.value.value.values;

                /**
                 * There are cases, when certain hierarchies have a level in common with other virtual hierarchy.
                 * On interactions which happen these hierarchies, we want to apply filter to each of them.
                 * Examples of hierarchies that share certain level: 311, 312, 316, ... (more from project reporting dashboard)
                 */
                const filtersForIntersectingHierarchies$: Observable<FilterData>[] = allFilterItems
                    .map(item => {
                        const hierarchyId = item.filterHint?.hierarchy?.id;
                        const intersectingHierarchyMetadata = intersectingHierarchies.find(
                            hierarchy => hierarchyId === hierarchy.hierarchyId,
                        );

                        if (!intersectingHierarchyMetadata) return null;

                        return this.translateHierarchyFilterToIntersectingHierarchy(
                            item,
                            intersectingHierarchyMetadata,
                            mainHierarchyNodes,
                            filterDataArray,
                            settings,
                        );
                    })
                    .filter(isNotNullable);

                return forkJoin([of(mainHierarchyFilter), ...filtersForIntersectingHierarchies$]);
            }),
        );
    }

    private processHierarchyFilterTypeHintForSingleHierarchy(
        matchedFilterItem: ResolvedListenToDefinition,
        hint: Exclude<ResolvedFilterHint['hierarchy'], undefined>,
        filterDataArray: FilterDataArray,
        keys: string[],
        eventKeywords: Keyword[],
        queryContext: MatchedListenToQueryContext,
        settings: {
            applyFilterToUpstreamHierarchyLevels: boolean;
        },
    ): Observable<FilterData> {
        const internalKey =
            queryContext.internalKey ||
            findMap(Object.entries(hint.internalKeyKeywords || {}), ([internalKey, keywords]) =>
                compareKeywords(eventKeywords, keywords) ? internalKey : null,
            );

        return forkJoin({
            values: this.masterdata.getHierarchyNodes(hint.id, internalKey, keys),
            hierarchy: this.masterdata.getHierarchyMetadata(hint.id),
        }).pipe(
            map(({ hierarchy, values }) => ({
                item: matchedFilterItem.item,
                value: FilterValue.HIERARCHY({
                    exclude: false,
                    value: {
                        hierarchyInternalKeys: hierarchy.internalKeys,
                        values: InteractionFilterTypeHintProcessor.formatHierarchyNodes(
                            matchedFilterItem,
                            hierarchy,
                            values,
                            filterDataArray,
                            settings,
                        ),
                    },
                }),
            })),
        );
    }

    /**
     * This method is used to convert a filter from one hierarchy to another.
     * It is used in cases when hierarchies share a common level, and we want to apply filter to
     * both hierarchies as a result of a selection.
     */
    private translateHierarchyFilterToIntersectingHierarchy(
        intersectingHierarchyFilterItem: ResolvedListenToDefinition,
        intersectingHierarchyMetadata: Hierarchy,
        mainHierarchyNodes: HierarchyFilterNode[],
        filterDataArray: FilterDataArray,
        settings: {
            applyFilterToUpstreamHierarchyLevels: boolean;
        },
    ) {
        // translate only nodes which belong to the target hierarchy levels
        // note we compare by `basedOnInternalKey` and not `internalKey`, because
        // hierarchies may use different subversions of the same dimension
        // (in that case the `internalKey`s will be different)
        const nodesToBeTranslated = mainHierarchyNodes.filter(({ basedOnInternalKey }) =>
            intersectingHierarchyMetadata.basedOnInternalKeys.includes(basedOnInternalKey),
        );

        const nodesToBeTranslatedByInternalKey = groupBy(
            nodesToBeTranslated,
            ({ basedOnInternalKey }) => basedOnInternalKey,
        );

        // fetch nodes on each particular hierarchy level
        const nodesPerLevelForIntersectingHierarchy$ = Object.entries(
            nodesToBeTranslatedByInternalKey,
        ).map(([basedOnInternalKey, nodes]) => {
            const keys = nodes.map(({ key }) => key);
            const internalKey = findMap(
                zip(
                    intersectingHierarchyMetadata.internalKeys,
                    intersectingHierarchyMetadata.basedOnInternalKeys,
                ),
                ([internalKey, key]) => (key === basedOnInternalKey ? internalKey : null),
            );

            return this.masterdata
                .getHierarchyNodes(intersectingHierarchyMetadata.hierarchyId, internalKey, keys)
                .pipe(
                    catchError((err: HttpError) =>
                        // if there is no nodes with given keys on particular level, ignore it
                        err.status === 404 ? of([]) : throwError(() => err),
                    ),
                );
        });

        return forkJoin(nodesPerLevelForIntersectingHierarchy$).pipe(
            defaultIfEmpty<
                Omit<HierarchyNode, 'children'>[][],
                Omit<HierarchyNode, 'children'>[][]
            >([]),
            map(nodesPerLevel => nodesPerLevel.flat()),
            map(values => ({
                item: intersectingHierarchyFilterItem.item,
                value: FilterValue.HIERARCHY({
                    exclude: false,
                    value: {
                        hierarchyInternalKeys: intersectingHierarchyMetadata.internalKeys,
                        values: InteractionFilterTypeHintProcessor.formatHierarchyNodes(
                            intersectingHierarchyFilterItem,
                            intersectingHierarchyMetadata,
                            values,
                            filterDataArray,
                            settings,
                        ),
                    },
                }),
            })),
        );
    }

    private static formatHierarchyNodes(
        matchedFilterItem: ResolvedListenToDefinition,
        hierarchy: Hierarchy,
        values: HierarchyFilterNode[],
        filterDataArray: FilterDataArray,
        settings: {
            applyFilterToUpstreamHierarchyLevels: boolean;
        },
    ) {
        const existingFilter = filterDataArray.find(({ item }) => item === matchedFilterItem.item);
        const existingNodes =
            existingFilter && FilterValue.is.HIERARCHY(existingFilter.value)
                ? existingFilter.value.value.values
                : [];

        return adjustFilterEffectInHierarchy(
            addAncestorsOfSelectedHierarchyNodes(values, hierarchy),
            values,
            existingNodes,
            hierarchy,
            settings,
        );
    }

    private processDateFilterTypeHint(
        matchedFilterItem: ResolvedListenToDefinition,
        allFilterItems: ResolvedListenToDefinitions,
        hint: Exclude<ResolvedFilterHint['date'], undefined>,
        _filterDataArray: FilterDataArray,
        keys: string[],
        _eventKeywords: Keyword[],
        _queryContext: MatchedListenToQueryContext,
        _settings: {
            applyFilterToUpstreamHierarchyLevels: boolean;
        },
    ): Observable<FilterData[]> {
        const validIsoString = tryParseDateToISO(head(keys));

        return validIsoString
            ? of([
                  {
                      item: matchedFilterItem.item,
                      value: FilterValue.DATE({ dateType: hint.type, value: validIsoString }),
                  },
              ])
            : throwError(() =>
                  createInteractionError({ keys, hint }, `Failed to create date filter`),
              );
    }

    private processDateRangeFilterTypeHint(
        matchedFilterItem: ResolvedListenToDefinition,
        allFilterItems: ResolvedListenToDefinitions,
        hint: Exclude<ResolvedFilterHint['dateRange'], undefined>,
        _filterDataArray: FilterDataArray,
        keys: string[],
        _eventKeywords: Keyword[],
        _queryContext: MatchedListenToQueryContext,
        _settings: {
            applyFilterToUpstreamHierarchyLevels: boolean;
        },
    ): Observable<FilterData[]> {
        const [from, to] = keys;
        const validIsoStringFrom = tryParseDateToISO(from);
        const validIsoStringTo = tryParseDateToISO(to);

        return validIsoStringFrom && validIsoStringTo
            ? of([
                  {
                      item: matchedFilterItem.item,
                      value: FilterValue.DATE_RANGE({
                          dateType: hint.type,
                          value: [validIsoStringFrom, validIsoStringTo],
                      }),
                  },
              ])
            : throwError(() =>
                  createInteractionError({ keys, hint }, `Failed to create date range filter`),
              );
    }

    private processScaleFilterTypeHint(
        matchedFilterItem: ResolvedListenToDefinition,
        allFilterItems: ResolvedListenToDefinitions,
        hint: Exclude<ResolvedFilterHint['scale'], undefined>,
        _filterDataArray: FilterDataArray,
        keys: string[],
        _eventKeywords: Keyword[],
        _queryContext: MatchedListenToQueryContext,
        _settings: {
            applyFilterToUpstreamHierarchyLevels: boolean;
        },
    ): Observable<FilterData[]> {
        const value = FilterValue.tryParseScale({ type: 'SCALE', value: head(keys) });

        return value
            ? of([{ item: matchedFilterItem.item, value }])
            : throwError(() =>
                  createInteractionError({ keys, hint }, `Failed to create scale filter`),
              );
    }

    private processSmartViewTagFilterTypeHint(
        matchedFilterItem: ResolvedListenToDefinition,
        allFilterItems: ResolvedListenToDefinitions,
        hint: Exclude<ResolvedFilterHint['smartViewTag'], undefined>,
        _filterDataArray: FilterDataArray,
        keys: string[],
        _eventKeywords: Keyword[],
        _queryContext: MatchedListenToQueryContext,
        _settings: {
            applyFilterToUpstreamHierarchyLevels: boolean;
        },
    ): Observable<FilterData[]> {
        const value = FilterValue.tryParseSmartViewTag({ viewTag: head(keys) });

        return value
            ? of([{ item: matchedFilterItem.item, value }])
            : throwError(() =>
                  createInteractionError({ keys, hint }, `Failed to create smart view tag filter`),
              );
    }

    private processRoleFilterTypeHint(
        matchedFilterItem: ResolvedListenToDefinition,
        allFilterItems: ResolvedListenToDefinitions,
        hint: Exclude<ResolvedFilterHint['role'], undefined>,
        _filterDataArray: FilterDataArray,
        keys: string[],
        _eventKeywords: Keyword[],
        _queryContext: MatchedListenToQueryContext,
        _settings: {
            applyFilterToUpstreamHierarchyLevels: boolean;
        },
    ): Observable<FilterData[]> {
        const value = FilterValue.tryParseRole({ type: 'ROLE', value: head(keys) });

        return value
            ? of([{ item: matchedFilterItem.item, value }])
            : throwError(() =>
                  createInteractionError({ keys, hint }, `Failed to create role filter`),
              );
    }
}

function tryParseDateToISO(a: unknown): string | null {
    const date = a && isString(a) ? new Date(a) : null;

    return date && !Number.isNaN(date.getTime()) ? date.toISOString() : null;
}
